/**
 * MODALE: LogCallModal
 * Modale pour consigner un appel téléphonique
 */

import { useAuth } from '@/hooks/useAuth';
import { useSupabase } from '@/hooks/useSupabase';
import { getEntityDisplayName } from '@/lib/timeline-logger';
import { logCallSchema, type LogCallInput } from '@/schemas/call';
import type { CallDirection, CallOutcome, EntityType } from '@/types/timeline';
import { CALL_DIRECTION_LABELS, CALL_OUTCOME_LABELS } from '@/types/timeline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { FormInput, FormSelect, FormTextarea, Toggle } from '../ui';
import { Form } from '../ui/form';
import { Modal, ModalFooter } from '../ui/modal';

interface LogCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: string;
  onSuccess?: () => void;
  /** Si l'appel est logué depuis un équipage, créer aussi un event sur l'équipage */
  relatedCrewId?: string;
  /** Nom du contact pour le titre de l'event équipage */
  relatedContactName?: string;
}

const DIRECTION_OPTIONS: { value: CallDirection; label: string }[] = [
  { value: 'outgoing', label: CALL_DIRECTION_LABELS.outgoing },
  { value: 'incoming', label: CALL_DIRECTION_LABELS.incoming },
];

const OUTCOME_OPTIONS: { value: CallOutcome; label: string }[] = [
  { value: 'answered', label: CALL_OUTCOME_LABELS.answered },
  { value: 'voicemail', label: CALL_OUTCOME_LABELS.voicemail },
  { value: 'no_answer', label: CALL_OUTCOME_LABELS.no_answer },
];

export function LogCallModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  onSuccess,
  relatedCrewId,
  relatedContactName,
}: LogCallModalProps) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const form = useForm<LogCallInput>({
    resolver: zodResolver(logCallSchema),
    defaultValues: {
      direction: 'outgoing',
      outcome: 'answered',
      duration: undefined,
      notes: '',
      create_task: false,
      task_date: '',
      task_time: '',
    },
  });

  const createTask = form.watch('create_task');
  const outcome = form.watch('outcome');
  const isSubmittingRef = useRef(false);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (data: LogCallInput) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      // Durée = 0 si répondeur, sinon la durée saisie
      const duration = data.outcome === 'voicemail' ? 0 : data.duration;

      // ✅ Récupérer le nom de l'entité pour un contexte clair
      const entityName = await getEntityDisplayName(supabase, entityType, entityId);
      const titlePrefix = entityName ? `${entityName} - ` : '';

      // 1. Créer l'événement timeline
      const { error: timelineError } = await supabase.from('timeline_events').insert({
        entity_type: entityType,
        entity_id: entityId,
        event_type: 'call_logged',
        title: `${titlePrefix}Appel ${CALL_DIRECTION_LABELS[data.direction].toLowerCase()} - ${CALL_OUTCOME_LABELS[data.outcome]}`,
        description: data.notes || null,
        created_by: user?.id || null,
        metadata: {
          direction: data.direction,
          outcome: data.outcome,
          duration,
          notes: data.notes || null,
          entity_name: entityName,
        },
      });

      if (timelineError) throw timelineError;

      // 1b. Si appel depuis un équipage, créer aussi un event sur l'équipage
      if (relatedCrewId) {
        const contactLabel = relatedContactName || entityName || 'un membre';
        await supabase.from('timeline_events').insert({
          entity_type: 'crew',
          entity_id: relatedCrewId,
          event_type: 'call_logged',
          title: `Appel ${CALL_DIRECTION_LABELS[data.direction].toLowerCase()} - ${contactLabel}`,
          description: data.notes || null,
          created_by: user?.id || null,
          metadata: {
            direction: data.direction,
            outcome: data.outcome,
            duration,
            notes: data.notes || null,
            contact_id: entityId,
            contact_name: contactLabel,
          },
        });
      }

      // 2. Créer la tâche si demandé
      if (data.create_task === true && data.task_date) {
        // Construire la date/heure complète EN HEURE LOCALE
        let dueDate: string;
        if (data.task_time) {
          // Créer une date avec l'heure locale saisie
          const [hours, minutes] = data.task_time.split(':').map(Number);
          const localDate = new Date(data.task_date);
          localDate.setHours(hours, minutes, 0, 0);
          // Convertir en ISO (inclut le fuseau horaire)
          dueDate = localDate.toISOString();
        } else {
          // Heure par défaut à 9h en heure locale
          const localDate = new Date(data.task_date);
          localDate.setHours(9, 0, 0, 0);
          dueDate = localDate.toISOString();
        }

        if (!user?.id) {
          console.error('Erreur: utilisateur non connecté, impossible de créer la tâche');
          toast.error('Erreur', {
            description: 'Impossible de créer la tâche: utilisateur non identifié',
          });
        } else {
          const { data: newTask, error: taskError } = await supabase
            .from('tasks')
            .insert({
              entity_type: entityType,
              entity_id: entityId,
              type: 'call',
              title: 'Relance téléphonique',
              notes: data.notes || null,
              due_date: dueDate,
              completed: false,
              assigned_to: user.id,
              created_by: user.id,
            })
            .select()
            .single();

          if (taskError) {
            console.error('Erreur lors de la création de la tâche:', taskError);
            toast.error('Erreur', {
              description: 'Impossible de créer la tâche de relance',
            });
          } else if (newTask) {
            // Log dans timeline
            await supabase.from('timeline_events').insert({
              entity_type: entityType,
              entity_id: entityId,
              event_type: 'task_created',
              title: `${titlePrefix}Relance téléphonique planifiée`,
              description: `Prévue le ${new Date(dueDate).toLocaleDateString('fr-FR')} à ${data.task_time || '09:00'}`,
              created_by: user?.id || null,
              metadata: {
                task_id: newTask.id,
                task_type: 'call',
                due_date: dueDate,
                entity_name: entityName,
              },
            });
            toast.success('Relance créée', {
              description: 'La tâche de relance a été planifiée',
            });
          }
        }
      }

      toast.success('Appel consigné', {
        description: "L'appel a été ajouté à l'historique",
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      console.error("Erreur lors de la consignation de l'appel:", error);
      toast.error('Erreur', {
        description: "Impossible de consigner l'appel",
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Consigner un appel"
      size="lg"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Valider"
          isSubmitting={form.formState.isSubmitting}
          formId="log-call-form"
        />
      }
    >
      <Form {...form}>
        <form id="log-call-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-6 pt-4">
            {/* Direction et Issue sur la même ligne */}
            <div className="grid grid-cols-2 gap-4">
              <FormSelect
                control={form.control as any}
                name="direction"
                label="Sens de l'appel"
                required
                options={DIRECTION_OPTIONS}
              />

              <FormSelect
                control={form.control as any}
                name="outcome"
                label="Issue"
                required
                options={OUTCOME_OPTIONS}
              />
            </div>

            {/* Durée (uniquement si appel répondu) */}
            {outcome === 'answered' && (
              <FormInput
                control={form.control as any}
                name="duration"
                label="Durée (en minutes)"
                type="number"
                placeholder="Ex: 5"
                valueAsNumber
              />
            )}

            {/* Notes */}
            <FormTextarea
              control={form.control as any}
              name="notes"
              label="Notes"
              placeholder="Notes sur l'appel..."
              rows={3}
            />

            {/* Section Créer une relance - avec cadre */}
            <div className="border border-zinc-200 rounded-lg p-4 flex flex-col gap-4">
              {/* Header avec Toggle */}
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="text-sm font-medium text-zinc-900 block">
                    Créer une relance téléphonique
                  </label>
                  <p className="text-base text-zinc-500 mt-1">Programmer un appel de suivi</p>
                </div>
                <Toggle
                  checked={createTask ?? false}
                  onChange={(checked) => form.setValue('create_task', checked)}
                />
              </div>

              {/* Champs Date et Heure - affichés uniquement si toggle activé */}
              {createTask && (
                <div className="border-t border-zinc-200 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      control={form.control as any}
                      name="task_date"
                      label="Date de la relance"
                      type="date"
                      required={createTask}
                    />
                    <FormInput
                      control={form.control as any}
                      name="task_time"
                      label="Heure (optionnel)"
                      type="time"
                      placeholder="09:00"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  );
}
